<div class="mb-3 p-3" data-testid="client-filter-section">

  <form [formGroup]="clientFilterForm">

    <!-- ── INCLUDE ──────────────────────────────────────────────────── -->
    <div class="mb-3">
      <!-- Label row -->
      <div class="flex align-items-center justify-content-between mb-2">
        <label class="font-medium text-700 text-xs uppercase"
          data-testid="include-filter-label">
          {{ 'CAMPAIGN.CLIENT_FILTER.INCLUDE' | translate }}
          @if (getIdCount('includeIds') > 0) {
            <span class="ml-2 font-normal normal-case text-primary">
              ({{ getIdCount('includeIds') }} {{ 'CAMPAIGN.CLIENT_FILTER.IDS' | translate }})
            </span>
          }
        </label>
        <div class="flex align-items-center gap-2">
          <!-- Hidden file input -->
          <input
            id="include-excel-upload"
            type="file"
            accept=".xlsx,.xls"
            class="hidden"
            (change)="onFileSelected($event, 'include')"
            aria-label="include-excel-upload-input"
            data-testid="include-excel-upload-input" />
          <!-- Upload button -->
          <button
            type="button"
            class="flex align-items-center gap-1 text-xs text-600 cursor-pointer border-none bg-transparent p-0"
            (click)="triggerUpload('include')"
            [disabled]="disabled()"
            pTooltip="{{ 'CAMPAIGN.CLIENT_FILTER.UPLOAD_TOOLTIP' | translate }}"
            tooltipPosition="top"
            aria-label="upload-include-excel-button"
            data-testid="upload-include-excel-button">
            <i class="pi pi-upload text-sm text-primary"></i>
            <span class="text-primary">{{ 'CAMPAIGN.CLIENT_FILTER.UPLOAD_EXCEL' | translate }}</span>
          </button>
          <!-- Download sample button -->
          <button
            type="button"
            class="flex align-items-center gap-1 text-xs text-600 cursor-pointer border-none bg-transparent p-0"
            (click)="downloadSampleExcel()"
            pTooltip="{{ 'CAMPAIGN.CLIENT_FILTER.DOWNLOAD_SAMPLE_TOOLTIP' | translate }}"
            tooltipPosition="top"
            aria-label="download-client-ids-sample-button"
            data-testid="download-client-ids-sample-button">
            <i class="pi pi-download text-sm text-600"></i>
            <span>{{ 'CAMPAIGN.CLIENT_FILTER.DOWNLOAD_SAMPLE' | translate }}</span>
          </button>
        </div>
      </div>

      <!-- Upload error -->
      @if (includeUploadError()) {
        <small class="text-red-500 text-xs block mb-1" data-testid="include-upload-error">
          <i class="pi pi-exclamation-triangle mr-1"></i>{{ includeUploadError() | translate }}
        </small>
      }

      <!-- Textarea -->
      <textarea
        id="include-ids-input"
        pInputTextarea
        formControlName="includeIds"
        rows="2"
        class="text-sm w-full"
        style="font-family: monospace; resize: vertical;"
        [placeholder]="'CAMPAIGN.CLIENT_FILTER.CLIENT_IDS_PLACEHOLDER' | translate"
        (blur)="onIdsBlur('includeIds')"
        [disabled]="disabled()"
        aria-label="include-client-ids-input"
        data-testid="include-client-ids-input">
      </textarea>

      @if (includeControl?.touched && includeControl?.invalid) {
        @if (includeControl?.hasError('invalidSeparator')) {
          <small class="text-red-500 text-xs block mt-1" data-testid="include-separator-error">
            {{ 'CAMPAIGN.CLIENT_FILTER.ERRORS.INVALID_SEPARATOR' | translate }}
          </small>
        }
        @if (includeControl?.hasError('invalidFormat')) {
          <small class="text-red-500 text-xs block mt-1" data-testid="include-format-error">
            {{ 'CAMPAIGN.CLIENT_FILTER.ERRORS.INVALID_FORMAT' | translate }}
          </small>
        }
        @if (includeControl?.hasError('duplicates')) {
          <small class="text-red-500 text-xs block mt-1" data-testid="include-duplicates-error">
            {{ 'CAMPAIGN.CLIENT_FILTER.ERRORS.DUPLICATES' | translate }}
          </small>
        }
      } @else {
        <small class="text-500 text-xs block mt-1" data-testid="include-help-text">
          {{ 'CAMPAIGN.CLIENT_FILTER.HELP_TEXT' | translate }}
        </small>
      }
    </div>

    <!-- Divider between Include and Exclude -->
    <div class="border-top-1 border-300 mb-3"></div>

    <!-- ── EXCLUDE ──────────────────────────────────────────────────── -->
    <div>
      <!-- Label row -->
      <div class="flex align-items-center justify-content-between mb-2">
        <label class="font-medium text-700 text-xs uppercase"
          data-testid="exclude-filter-label">
          {{ 'CAMPAIGN.CLIENT_FILTER.EXCLUDE' | translate }}
          @if (getIdCount('excludeIds') > 0) {
            <span class="ml-2 font-normal normal-case text-red-500">
              ({{ getIdCount('excludeIds') }} {{ 'CAMPAIGN.CLIENT_FILTER.IDS' | translate }})
            </span>
          }
        </label>
        <div class="flex align-items-center gap-2">
          <!-- Hidden file input -->
          <input
            id="exclude-excel-upload"
            type="file"
            accept=".xlsx,.xls"
            class="hidden"
            (change)="onFileSelected($event, 'exclude')"
            aria-label="exclude-excel-upload-input"
            data-testid="exclude-excel-upload-input" />
          <!-- Upload button -->
          <button
            type="button"
            class="flex align-items-center gap-1 text-xs text-600 cursor-pointer border-none bg-transparent p-0"
            (click)="triggerUpload('exclude')"
            [disabled]="disabled()"
            pTooltip="{{ 'CAMPAIGN.CLIENT_FILTER.UPLOAD_TOOLTIP' | translate }}"
            tooltipPosition="top"
            aria-label="upload-exclude-excel-button"
            data-testid="upload-exclude-excel-button">
            <i class="pi pi-upload text-sm text-primary"></i>
            <span class="text-primary">{{ 'CAMPAIGN.CLIENT_FILTER.UPLOAD_EXCEL' | translate }}</span>
          </button>
          <!-- Download sample button -->
          <button
            type="button"
            class="flex align-items-center gap-1 text-xs text-600 cursor-pointer border-none bg-transparent p-0"
            (click)="downloadSampleExcel()"
            pTooltip="{{ 'CAMPAIGN.CLIENT_FILTER.DOWNLOAD_SAMPLE_TOOLTIP' | translate }}"
            tooltipPosition="top"
            aria-label="download-exclude-sample-button"
            data-testid="download-exclude-sample-button">
            <i class="pi pi-download text-sm text-600"></i>
            <span>{{ 'CAMPAIGN.CLIENT_FILTER.DOWNLOAD_SAMPLE' | translate }}</span>
          </button>
        </div>
      </div>

      <!-- Upload error -->
      @if (excludeUploadError()) {
        <small class="text-red-500 text-xs block mb-1" data-testid="exclude-upload-error">
          <i class="pi pi-exclamation-triangle mr-1"></i>{{ excludeUploadError() | translate }}
        </small>
      }

      <!-- Textarea -->
      <textarea
        id="exclude-ids-input"
        pInputTextarea
        formControlName="excludeIds"
        rows="2"
        class="text-sm w-full"
        style="font-family: monospace; resize: vertical;"
        [placeholder]="'CAMPAIGN.CLIENT_FILTER.CLIENT_IDS_PLACEHOLDER' | translate"
        (blur)="onIdsBlur('excludeIds')"
        [disabled]="disabled()"
        aria-label="exclude-client-ids-input"
        data-testid="exclude-client-ids-input">
      </textarea>

      @if (excludeControl?.touched && excludeControl?.invalid) {
        @if (excludeControl?.hasError('invalidSeparator')) {
          <small class="text-red-500 text-xs block mt-1" data-testid="exclude-separator-error">
            {{ 'CAMPAIGN.CLIENT_FILTER.ERRORS.INVALID_SEPARATOR' | translate }}
          </small>
        }
        @if (excludeControl?.hasError('invalidFormat')) {
          <small class="text-red-500 text-xs block mt-1" data-testid="exclude-format-error">
            {{ 'CAMPAIGN.CLIENT_FILTER.ERRORS.INVALID_FORMAT' | translate }}
          </small>
        }
        @if (excludeControl?.hasError('duplicates')) {
          <small class="text-red-500 text-xs block mt-1" data-testid="exclude-duplicates-error">
            {{ 'CAMPAIGN.CLIENT_FILTER.ERRORS.DUPLICATES' | translate }}
          </small>
        }
      } @else {
        <small class="text-500 text-xs block mt-1" data-testid="exclude-help-text">
          {{ 'CAMPAIGN.CLIENT_FILTER.HELP_TEXT' | translate }}
        </small>
      }
    </div>

  </form>
</div>
